import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@jbnl/api-client";
import i18n from "@/i18n";
import { api } from "@/lib/api";

export const wedstrijdboekjesKey = (seizoenId: string) =>
  ["beheer", "wedstrijdboekjes", seizoenId] as const;

/** Its own key: whether a boekje has anything new to print costs a full read of its pages. */
export const wedstrijdboekjeKey = (id: string) =>
  ["beheer", "wedstrijdboekje", id] as const;

export type Wedstrijdboekje = components["schemas"]["WedstrijdboekjeDto"];
export type Wedstrijdboekjedetail = components["schemas"]["WedstrijdboekjeDetailDto"];
export type Versie = components["schemas"]["WedstrijdboekjegeneratieDto"];
export type OngedekteDag = components["schemas"]["OngedekteDagDto"];

/** The API serialises `GeneratieStatus` as a number, so the SPA names the three digits once. */
export const VERSIESTATUS = { bezig: 1, geslaagd: 2, mislukt: 3 } as const;

/** The API answers RFC 7807, so a refusal carries a Dutch sentence the beheerder can act on. */
function problem(error: unknown, response: Response, fallback: string) {
  const title = (error as { title?: string } | undefined)?.title;
  return new Error(title ?? `${fallback} (${response.status}).`);
}

/**
 * Polls only while a version is being made: generating runs in the jobs service, so the row
 * would otherwise sit on "bezig" until the page was reloaded.
 */
export function useWedstrijdboekjes(seizoenId: string | undefined) {
  return useQuery({
    queryKey: wedstrijdboekjesKey(seizoenId ?? ""),
    queryFn: !seizoenId
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/seizoenen/{seizoenId}/wedstrijdboekjes",
            { params: { path: { seizoenId } } },
          );
          if (!data) throw problem(error, response, i18n.t("wedstrijdboekjes:errors.ophalen"));
          return data;
        },
    refetchInterval: (query) =>
      query.state.data?.boekjes.some((boekje) =>
        boekje.generaties.some((versie) => versie.status === VERSIESTATUS.bezig),
      )
        ? 2000
        : false,
  });
}

/** Polls on the same rule the list does, and for the same reason. */
export function useWedstrijdboekje(id: string | undefined) {
  return useQuery({
    queryKey: wedstrijdboekjeKey(id ?? ""),
    queryFn: !id
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/wedstrijdboekjes/{id}",
            { params: { path: { id } } },
          );
          if (!data) throw problem(error, response, i18n.t("wedstrijdboekjes:errors.ophalen"));
          return data;
        },
    refetchInterval: (query) =>
      query.state.data?.boekje.generaties.some(
        (versie) => versie.status === VERSIESTATUS.bezig,
      )
        ? 2000
        : false,
  });
}

export type Boekjevelden = {
  name: string;
  titel: string;
  voettekst: string;
  metStaandeTekst: boolean;
  wedstrijddagIds: string[];
  klasseIds: string[];
};

export function useCreateWedstrijdboekje(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: Boekjevelden) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/seizoenen/{seizoenId}/wedstrijdboekjes",
        { params: { path: { seizoenId } }, body },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijdboekjes:errors.aanmaken"));
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: wedstrijdboekjesKey(seizoenId) }),
  });
}

export function useUpdateWedstrijdboekje(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...body }: Boekjevelden & { id: string }) => {
      const { data, error, response } = await api.PUT("/api/beheer/wedstrijdboekjes/{id}", {
        params: { path: { id } },
        body,
      });
      if (!data) throw problem(error, response, i18n.t("wedstrijdboekjes:errors.opslaan"));
      return data;
    },
    onSuccess: (_, { id }) => vernieuw(queryClient, seizoenId, id),
  });
}

export function useDeleteWedstrijdboekje(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE("/api/beheer/wedstrijdboekjes/{id}", {
        params: { path: { id } },
      });
      if (!response.ok) {
        throw problem(error, response, i18n.t("wedstrijdboekjes:errors.verwijderen"));
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: wedstrijdboekjesKey(seizoenId) }),
  });
}

/** Queues the next version. The row comes back as bezig and the query above starts polling. */
export function useGenereerWedstrijdboekje(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/wedstrijdboekjes/{id}/versies",
        { params: { path: { id } } },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijdboekjes:errors.genereren"));
      return data;
    },
    onSuccess: (_, id) => vernieuw(queryClient, seizoenId, id),
  });
}

export function useVerwijderNieuwsteVersie(seizoenId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error, response } = await api.DELETE(
        "/api/beheer/wedstrijdboekjes/{id}/versies/nieuwste",
        { params: { path: { id } } },
      );
      if (!data) throw problem(error, response, i18n.t("wedstrijdboekjes:errors.versieWeg"));
      return data;
    },
    onSuccess: (_, id) => vernieuw(queryClient, seizoenId, id),
  });
}

/**
 * Both reads, because every write moves both: refreshing only the list left the notice above the
 * button contradicting the row under it.
 */
function vernieuw(
  queryClient: ReturnType<typeof useQueryClient>,
  seizoenId: string,
  id: string,
) {
  void queryClient.invalidateQueries({ queryKey: wedstrijdboekjesKey(seizoenId) });
  void queryClient.invalidateQueries({ queryKey: wedstrijdboekjeKey(id) });
}

/**
 * A plain link, not a fetch: the session is a cookie on this origin, so the browser downloads it
 * under the Content-Disposition filename the API sets - the name JBNL publishes it under.
 */
export function versieHref(versieId: string, formaat: "Pdf" | "Docx") {
  return `/api/beheer/wedstrijdboekjes/versies/${versieId}/document?formaat=${formaat}`;
}
